import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { base44 } from "@/api/base44Client";
import AdFunnelAnalysing from "@/components/AdFunnelAnalysing";
import AdFunnelLegalModal from "@/components/AdFunnelLegalModal";

const BG_IMAGE = "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/e064767ed_generated_image.png";
const LOGO_DARK = "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/4e392029e_TruestaysLogoDark.png";

const BED_OPTS = ["Studio", "1", "2", "3", "4", "5", "6", "7+"];
const CONDITIONS = ["Basic", "Average", "Good", "Luxury"];

const inputStyle = {
  width: "100%", boxSizing: "border-box", border: "1.5px solid #E0E0E0", borderRadius: 10,
  padding: "11px 14px", fontFamily: "'Inter', sans-serif", fontSize: 16, color: "#1A1A1A",
  background: "#fff", outline: "none", transition: "border-color 0.2s",
};

function Star() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="#fff" stroke="#fff" strokeWidth="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>;
}
function GoogleG() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-label="Google">
      <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

const LegalLinks = ({ onOpen }) => (
  <div style={{ display: "flex", gap: 16, justifyContent: "center", marginTop: 10 }}>
    <button type="button" onClick={() => onOpen("terms")} style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: "#B0B0B0", background: "none", border: "none", cursor: "pointer", padding: 0 }}>Terms &amp; Conditions</button>
    <button type="button" onClick={() => onOpen("privacy")} style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: "#B0B0B0", background: "none", border: "none", cursor: "pointer", padding: 0 }}>Privacy Policy</button>
  </div>
);

export default function AdFunnel() {
  const [step, setStep] = useState(0); // 0 address, 1 beds, 2 condition, 3 analysing, 4 contact, 5 success
  const [form, setForm] = useState({ address: "", bedrooms: "", condition: "", name: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [legal, setLegal] = useState(null);
  const advanceTimer = useRef(null);

  useEffect(() => {
    document.title = "Free Airbnb Income Calculator | Truestays";
    let m = document.querySelector('meta[name="robots"]');
    if (!m) { m = document.createElement("meta"); m.name = "robots"; document.head.appendChild(m); }
    m.setAttribute("content", "noindex, nofollow");
    return () => { clearTimeout(advanceTimer.current); };
  }, []);

  // Auto-advance from analysing -> contact
  useEffect(() => {
    if (step === 3) {
      advanceTimer.current = setTimeout(() => setStep(4), 2600);
    }
    return () => clearTimeout(advanceTimer.current);
  }, [step]);

  // Auto-redirect home from success after 5s
  useEffect(() => {
    if (step !== 5) return;
    const t = setTimeout(() => { window.location.href = "/"; }, 5000);
    return () => clearTimeout(t);
  }, [step]);

  const validatePhone = (phone) => {
    const d = phone.replace(/\s/g, "").replace(/-/g, "");
    return /^((\+44|0044|0)[1-9][0-9]{7,9})$/.test(d) || (d.length >= 10 && d.length <= 15);
  };

  const selectBed = (v) => {
    setForm(p => ({ ...p, bedrooms: v }));
    clearTimeout(advanceTimer.current);
    advanceTimer.current = setTimeout(() => setStep(2), 260);
  };
  const selectCondition = (v) => {
    setForm(p => ({ ...p, condition: v }));
    clearTimeout(advanceTimer.current);
    advanceTimer.current = setTimeout(() => setStep(3), 260);
  };

  const handleAddressContinue = () => {
    if (!form.address.trim()) { setErrors(e => ({ ...e, address: "Please enter your address" })); return; }
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = {};
    if (!form.name.trim()) err.name = "Required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Valid email required";
    if (form.phone.trim() && !validatePhone(form.phone)) err.phone = "Enter a valid phone number";
    setErrors(err);
    if (Object.keys(err).length) return;

    setSubmitting(true);
    const formData = {
      fullName: form.name, email: form.email, phone: form.phone, phoneCode: "+44",
      address: form.address, bedrooms: form.bedrooms, condition: form.condition,
      mode: "ad-funnel", type: "valuation", sourcePage: "/free-airbnb-calculator",
      marketingConsent: "Meta ads",
    };
    try {
      await base44.functions.invoke("saveLeadToSheet", { formData, uploadedFiles: [] });
    } catch (_) {}
    try {
      const body = [
        "=== AD FUNNEL LEAD ===",
        `Name: ${form.name}`, `Email: ${form.email}`, `Phone: ${form.phone}`,
        `Address: ${form.address}`, `Bedrooms: ${form.bedrooms}`, `Condition: ${form.condition}`,
        `Source: Meta ads`,
      ].join("\n");
      await base44.functions.invoke("sendLeadEmail", { subject: `Ad funnel lead – ${form.name}`, body });
    } catch (_) {}
    try {
      await base44.functions.invoke("sendConfirmationEmail", {
        to: form.email, name: form.name, enquiryType: "valuation",
        fields: [
          { label: "Name", value: form.name }, { label: "Email", value: form.email },
          { label: "Phone", value: form.phone }, { label: "Property address", value: form.address },
          { label: "Bedrooms", value: form.bedrooms }, { label: "Condition", value: form.condition },
        ],
      });
    } catch (_) {}
    setSubmitting(false);
    setStep(5);
  };

  const totalSteps = 4;
  const progress = step >= 4 ? totalSteps : step + 1;

  return (
    <div style={{ position: "fixed", inset: 0, height: "100dvh", display: "flex", flexDirection: "column", overflow: "hidden", background: "#000", fontFamily: "'Inter', sans-serif" }}>
      {/* Background image + overlay */}
      <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${BG_IMAGE})`, backgroundSize: "cover", backgroundPosition: "center" }} />
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)" }} />

      {/* Top bar */}
      <div style={{ position: "relative", zIndex: 2, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", paddingTop: "calc(14px + env(safe-area-inset-top))" }}>
        <Link to="/" style={{ display: "inline-flex", alignItems: "center", textDecoration: "none" }}>
          <img src={LOGO_DARK} alt="Truestays" style={{ height: 26, width: "auto", display: "block", filter: "brightness(0) invert(1)" }} />
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <div style={{ display: "flex", gap: 1 }}>{[1,2,3,4,5].map(i => <Star key={i} />)}</div>
          <GoogleG />
        </div>
      </div>

      {/* Hero text — centered in the empty space above the card */}
      <div style={{ position: "relative", zIndex: 2, flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "8px 24px" }}>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.9)", margin: "0 0 10px" }}>Airbnb &amp; Short-Let Management</p>
        <h1 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(30px, 8vw, 44px)", lineHeight: 1.1, letterSpacing: "-0.02em", color: "#fff", margin: 0 }}>How much could your property earn on Airbnb?</h1>
      </div>

      {/* Card */}
      <div style={{ position: "relative", zIndex: 2, marginTop: "auto", width: "100%", maxWidth: 480, marginLeft: "auto", marginRight: "auto", background: "#fff", borderRadius: "24px 24px 0 0", padding: "16px 22px calc(14px + env(safe-area-inset-bottom))", display: "flex", flexDirection: "column", flex: "0 0 auto", maxHeight: "58dvh", minHeight: 0, boxSizing: "border-box", overflow: "hidden" }}>
        {/* Progress bars */}
        <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i < progress ? "#000" : "#D3D3D3", transition: "background 0.3s" }} />
          ))}
        </div>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#888", margin: "0 0 10px" }}>Free Airbnb Income Calculator</p>

        {/* Step content */}
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="s0" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }}>
                <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 18, color: "#1A1A1A", margin: "0 0 10px", letterSpacing: "-0.01em" }}>Where is your property?</h2>
                <input value={form.address} onChange={e => { setForm(p => ({ ...p, address: e.target.value })); if (errors.address) setErrors(x => ({ ...x, address: undefined })); }} placeholder="Address or Postcode" autoComplete="off" style={{ ...inputStyle, borderColor: errors.address ? "#4A6DC7" : "#E0E0E0" }} />
                {errors.address && <p style={{ color: "#4A6DC7", fontSize: 11, marginTop: 4, margin: "4px 0 0" }}>{errors.address}</p>}
                <button type="button" onClick={handleAddressContinue} style={{ marginTop: 14, width: "100%", background: "#000", color: "#fff", border: "none", borderRadius: 10, padding: "14px", fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>Continue</button>
              </motion.div>
            )}
            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }}>
                <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 18, color: "#1A1A1A", margin: "0 0 10px", letterSpacing: "-0.01em" }}>How many bedrooms?</h2>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                  {BED_OPTS.map(b => {
                    const sel = form.bedrooms === b;
                    return (
                      <button key={b} type="button" onClick={() => selectBed(b)} style={{ padding: "11px 4px", border: sel ? "2px solid #000" : "1.5px solid #E0E0E0", borderRadius: 10, background: sel ? "#FAFAFA" : "#fff", fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 600, color: "#1A1A1A", cursor: "pointer", transition: "border-color 0.15s" }}>{b}</button>
                    );
                  })}
                </div>
              </motion.div>
            )}
            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }}>
                <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 18, color: "#1A1A1A", margin: "0 0 10px", letterSpacing: "-0.01em" }}>What condition is the property in?</h2>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {CONDITIONS.map(c => {
                    const sel = form.condition === c;
                    return (
                      <button key={c} type="button" onClick={() => selectCondition(c)} style={{ padding: "12px 8px", border: sel ? "2px solid #000" : "1.5px solid #E0E0E0", borderRadius: 10, background: sel ? "#FAFAFA" : "#fff", fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 600, color: "#1A1A1A", cursor: "pointer", transition: "border-color 0.15s" }}>{c}</button>
                    );
                  })}
                </div>
              </motion.div>
            )}
            {step === 3 && (
              <motion.div key="s3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: 180 }}>
                <AdFunnelAnalysing />
              </motion.div>
            )}
            {step === 4 && (
              <motion.form key="s4" onSubmit={handleSubmit} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }}>
                <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 18, color: "#1A1A1A", margin: "0 0 10px", letterSpacing: "-0.01em" }}>How can we send the estimates?</h2>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div>
                    <input value={form.name} onChange={e => { setForm(p => ({ ...p, name: e.target.value })); if (errors.name) setErrors(x => ({ ...x, name: undefined })); }} placeholder="Name" style={{ ...inputStyle, borderColor: errors.name ? "#4A6DC7" : "#E0E0E0" }} />
                    {errors.name && <p style={{ color: "#4A6DC7", fontSize: 11, margin: "3px 2px 0" }}>{errors.name}</p>}
                  </div>
                  <div>
                    <input type="email" value={form.email} onChange={e => { setForm(p => ({ ...p, email: e.target.value })); if (errors.email) setErrors(x => ({ ...x, email: undefined })); }} placeholder="Email address" style={{ ...inputStyle, borderColor: errors.email ? "#4A6DC7" : "#E0E0E0" }} />
                    {errors.email && <p style={{ color: "#4A6DC7", fontSize: 11, margin: "3px 2px 0" }}>{errors.email}</p>}
                  </div>
                  <div>
                    <input type="tel" value={form.phone} onChange={e => { setForm(p => ({ ...p, phone: e.target.value })); if (errors.phone) setErrors(x => ({ ...x, phone: undefined })); }} placeholder="Phone (optional)" style={{ ...inputStyle, borderColor: errors.phone ? "#4A6DC7" : "#E0E0E0" }} />
                    {errors.phone && <p style={{ color: "#4A6DC7", fontSize: 11, margin: "3px 2px 0" }}>{errors.phone}</p>}
                  </div>
                </div>
                <button type="submit" disabled={submitting} style={{ marginTop: 12, width: "100%", background: "#000", color: "#fff", border: "none", borderRadius: 10, padding: "12px", fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 700, cursor: "pointer", opacity: submitting ? 0.6 : 1 }}>{submitting ? "Submitting…" : "Submit"}</button>
              </motion.form>
            )}
            {step === 5 && (
              <motion.div key="s5" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }} style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" style={{ marginBottom: 10 }}><circle cx="12" cy="12" r="11" fill="#eb2f5b" /><path d="M7 12.5l3.2 3.2L17 9" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 20, color: "#1A1A1A", margin: "0 0 6px" }}>Request Received</h2>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: "#555", lineHeight: 1.5, margin: "0 0 12px" }}>Our team will email your bespoke Airbnb income estimates within 24 hours.</p>
                <button type="button" onClick={() => { window.location.href = "/"; }} style={{ width: "100%", background: "#000", color: "#fff", border: "none", borderRadius: 10, padding: "12px", fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>Continue</button>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: "#B0B0B0", margin: "8px 0 0" }}>Redirecting you home shortly…</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {step >= 4 && <LegalLinks onOpen={setLegal} />}
      </div>
      <AdFunnelLegalModal which={legal} onClose={() => setLegal(null)} />
    </div>
  );
}